/**
 * PUBLIC · open roles. One component, two routes:
 *   /jobs                 every requirement shared publicly and still open (all companies) — the
 *                         TalentHop Careers page people share on WhatsApp / LinkedIn
 *   /careers/:companySlug one company's open roles
 * No login. Reads GET /public/jobs and GET /public/companies/{slug}; nothing here is personal data.
 * A card (or View job) opens /jobs/:slug; Apply now opens it at the apply form (#apply). Each job
 * URL carries its own link-preview tags (apps/web/api/job-og).
 */
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import "../../styles/public-jobs.css";

export interface PublicJobSummary {
  slug: string;
  title: string;
  companyName: string;
  companySlug: string | null;
  location: string | null;
  workplaceMode: string | null;
  department: string | null;
  /** Absent on an older API. */
  experience?: string | null;
  excerpt?: string | null;
  postedAt: string | null;
}

/** The public frame: TalentHop brand bar, page, footer. `wide` is kept for callers; every page is wide now. */
export function PublicShell({ children, wide = false }: { children: ReactNode; wide?: boolean }): ReactNode {
  return (
    <div className={`pj-page pj-wide${wide ? " pj-wide-detail" : ""}`}>
      <header className="pj-top">
        <Link to="/jobs" className="pj-brand" aria-label="TalentHop Careers — all open positions">
          <img src="/brand/talenthop-mark.svg" alt="" width={34} height={34} className="pj-brand-mark" />
          <span>
            TalentHop <span className="pj-brand-sub">Careers</span>
          </span>
        </Link>
        <Link to="/jobs" className="pj-top-link">
          All open positions
        </Link>
      </header>
      <main className="pj-wrap">{children}</main>
      <footer className="pj-foot">
        <span>© TalentHop · app.talenthopai.com</span>
        <span>Hiring is decided by people. AI helps them read CVs and interviews.</span>
      </footer>
    </div>
  );
}

function posted(iso: string | null): string | null {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "Posted today";
  if (days === 1) return "Posted yesterday";
  if (days < 30) return `Posted ${days} days ago`;
  return `Posted ${new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
}

export function JobCard({ job, showCompany = true }: { job: PublicJobSummary; showCompany?: boolean }): ReactNode {
  const navigate = useNavigate();
  const href = `/jobs/${job.slug}`;
  const facts = [job.location, job.workplaceMode, job.department, job.experience].filter((f): f is string => !!f);
  const when = posted(job.postedAt);
  return (
    <article
      className="pj-card"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a, button")) return;
        navigate(href);
      }}
    >
      <div className="pj-card-head">
        <span className="pj-card-logo" aria-hidden="true">
          {(job.companyName || "?").trim().charAt(0).toUpperCase()}
        </span>
        <div className="pj-card-titles">
          <h3>
            <Link to={href}>{job.title}</Link>
          </h3>
          {showCompany && <div className="pj-co">{job.companyName}</div>}
        </div>
      </div>
      {facts.length > 0 && (
        <ul className="pj-card-facts">
          {facts.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
      {job.excerpt && <p className="pj-card-excerpt">{job.excerpt}</p>}
      <div className="pj-card-foot">
        <span className="pj-card-when">{when ?? ""}</span>
        <span className="pj-card-actions">
          <Link to={href} className="btn sm">
            View job
          </Link>
          <Link to={`${href}#apply`} className="btn sm primary">
            Apply now
          </Link>
        </span>
      </div>
    </article>
  );
}

const uniq = (xs: (string | null | undefined)[]): string[] =>
  [...new Set(xs.filter((x): x is string => !!x && !!x.trim()).map((x) => x.trim()))].sort((a, b) => a.localeCompare(b));

export function PublicJobs(): ReactNode {
  const { companySlug } = useParams();
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");
  const [mode, setMode] = useState("");

  const list = useQuery({
    queryKey: ["public-jobs", companySlug ?? "all"],
    queryFn: async () => {
      if (companySlug) {
        return api.get<{ company: { name: string; website: string | null; industry: string | null }; jobs: PublicJobSummary[] }>(
          `/public/companies/${companySlug}`,
        );
      }
      const r = await api.get<{ jobs: PublicJobSummary[] }>(`/public/jobs`);
      return { company: null, jobs: r.jobs };
    },
    retry: false,
  });

  const company = list.data?.company ?? null;
  const all = useMemo(() => list.data?.jobs ?? [], [list.data]);
  const locations = useMemo(() => uniq(all.map((j) => j.location)), [all]);
  const departments = useMemo(() => uniq(all.map((j) => j.department)), [all]);
  const modes = useMemo(() => uniq(all.map((j) => j.workplaceMode)), [all]);

  // Search + filters run on the loaded list: instant, and the same on both routes.
  const jobs = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter(
      (j) =>
        (!needle ||
          [j.title, j.companyName, j.department, j.location].some((v) => v?.toLowerCase().includes(needle))) &&
        (!location || j.location === location) &&
        (!department || j.department === department) &&
        (!mode || j.workplaceMode === mode),
    );
  }, [all, q, location, department, mode]);
  const filtered = !!(q.trim() || location || department || mode);
  const companies = new Set(all.map((j) => j.companyName)).size;

  return (
    <PublicShell>
      <section className="pj-list-hero">
        <div className="pj-list-hero-text">
          <span className="pj-eyebrow">{company ? "Careers" : "Open positions"}</span>
          <h1>{company ? `Join ${company.name}` : "Find your next opportunity"}</h1>
          <p>
            {company
              ? [company.industry, company.website].filter(Boolean).join(" · ") ||
                "Explore our open positions and apply in minutes."
              : "Explore open positions from teams hiring on TalentHop, and apply in minutes."}
          </p>
          {list.isSuccess && all.length > 0 && (
            <div className="pj-list-stats">
              <span>
                <b>{all.length}</b> open {all.length === 1 ? "role" : "roles"}
              </span>
              {!company && companies > 1 && (
                <span>
                  <b>{companies}</b> companies
                </span>
              )}
            </div>
          )}
        </div>
        <div className="pj-search-wrap">
          <input
            className="pj-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={company ? "Search by job title or role" : "Search by job title, role or company"}
            aria-label="Search open positions"
          />
        </div>
      </section>

      {all.length > 0 && (locations.length > 1 || departments.length > 1 || modes.length > 1) && (
        <div className="pj-filters" role="group" aria-label="Filter positions">
          {locations.length > 1 && (
            <select value={location} onChange={(e) => setLocation(e.target.value)} aria-label="Location">
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          )}
          {departments.length > 1 && (
            <select value={department} onChange={(e) => setDepartment(e.target.value)} aria-label="Department">
              <option value="">All departments</option>
              {departments.map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
          )}
          {modes.length > 1 && (
            <select value={mode} onChange={(e) => setMode(e.target.value)} aria-label="Work type">
              <option value="">All work types</option>
              {modes.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          )}
          {filtered && (
            <button
              type="button"
              className="btn sm ghost"
              onClick={() => {
                setQ("");
                setLocation("");
                setDepartment("");
                setMode("");
              }}
            >
              Clear filters
            </button>
          )}
          <span className="pj-filters-count">
            {jobs.length} of {all.length}
          </span>
        </div>
      )}

      {list.isLoading && <p className="pj-muted">Loading open positions…</p>}
      {list.isError && <p className="pj-muted">This page isn’t available.</p>}
      {list.isSuccess && jobs.length === 0 && (
        <div className="pj-empty">
          {filtered ? "No positions match that search or filter." : "There are no open positions right now — check back soon."}
        </div>
      )}
      <div className="pj-grid">
        {jobs.map((j) => (
          <JobCard key={j.slug} job={j} showCompany={!company} />
        ))}
      </div>
    </PublicShell>
  );
}
